import React, {useState, useEffect} from 'react';
import {ObjectId} from 'bson';
import {
  ScrollView,
  View,
  ImageBackground,
  Text,
  Pressable,
  Image,
} from 'react-native';
import moment from 'moment';
import DynamicSelect from '../DynamicControls/DynamicSelect';
import Icon from '../Shared/Icon';
import {Icons} from '../../Constants/Icons';
import CustomPressable from '../Shared/CustomPressable';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {Images} from '../../Constants/Images';
import Colors from '../../Styles/Shared/Colors';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import SearchBox from '../Shared/SearchBox';
import {ProjectSitesStyleSheet} from '../../Styles/ProjectSiteStyles';
import {Navigation} from '../../Constants/Navigation';
import {navigationRef} from '../Shared/RootNavigation';
import {PERSON_STATUSES} from '../../Constants/AppConstants';
import Paginator from '../Shared/Paginator';

type PeopleListProps = {navigation: any};

export type PersonRecord = {
  id: string;
  sqlServerId: string;
  firstName: string;
  lastName: string;
  info: string;
  inactiveDate?: Date;
  userStatus?: string;
};

const PeopleList = (props: PeopleListProps): React.ReactElement => {
  const [search, setSearch] = useState('');
  const [records, setRecords] = useState<PersonRecord[]>([]);

  const [filteredStatus, setFilteredStatus] = useState([
    PERSON_STATUSES.ACTIVE,
    PERSON_STATUSES.ACCESS,
    PERSON_STATUSES.PENDING,
    PERSON_STATUSES.DISABLED,
  ]);
  const [filteredOrderBy, setFilteredOrderBy] = useState('First Name');

  const DISPLAY_INCREMENT = 5;
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  const [peoplePageData, setPeoplePageData] = useState<PersonRecord[]>([]);

  const {people, SQLUsers} = usePowerSyncData();

  useEffect(() => {
    fetchPeople();
  }, [people, SQLUsers]);

  useEffect(() => {
    if (navigationRef.current) {
      const unsubscribe = navigationRef.current.addListener('state', () => {
        fetchPeople();
      });

      return unsubscribe;
    }
  }, []);

  useEffect(() => {
    setExpandedItems([]);

    fetchPeople();
  }, [search]);

  const fetchPeople = async () => {
    let filteredRecords = people.filter(p => !p.isDeleted);

    // Only grab users of current filtered person rows
    let sqlServerIds = filteredRecords.map(v => v.SQLServerId);
    let filteredUsers = SQLUsers.sort(
      (a, b) => b.dataHubVersion - a.dataHubVersion,
    )
      .filter(u => !u.isDeleted && sqlServerIds.includes(u.SQLServerPersonId))
      .map(u => ({
        sqlServerPersonId: u.SQLServerPersonId,
        statusObj: u.statusObj,
      }));

    let mappedRecords: PersonRecord[] = [];
    filteredRecords.forEach(p => {
      let personInfo = 'Email: ' + (p.email ? p.email : '(not provided)');
      if (p.phone) personInfo += ', Phone: ' + p.phone;

      let status = '';
      let user = filteredUsers.find(u => u.sqlServerPersonId === p.SQLServerId);
      if (user && user.statusObj) status = user.statusObj.name ?? '';

      mappedRecords.push({
        id: p._id,
        sqlServerId: p.SQLServerId,
        firstName: p.firstName!,
        lastName: p.lastName!,
        info: personInfo,
        inactiveDate: p.inactiveDate!,
        userStatus: status,
      });
    });

    setRecords(mappedRecords);
  };

  const goToPerson = (personId: string | null) => {
    props.navigation.push(Navigation.EDITPERSON, {
      personId: personId,
    });
  };

  function sortItems(itemsToSort: PersonRecord[]): PersonRecord[] {
    if (filteredOrderBy === 'First Name')
      return itemsToSort.sort((a, b) =>
        ((a.firstName + ' ' + a.lastName)?.toLowerCase() ?? '') >
        ((b.firstName + ' ' + b.lastName).toLowerCase() ?? '')
          ? 1
          : ((a.firstName + ' ' + a.lastName)?.toLowerCase() ?? '') <
              ((b.firstName + ' ' + b.lastName)?.toLowerCase() ?? '')
            ? -1
            : 0,
      );
    else
      return itemsToSort.sort((a, b) =>
        ((a.lastName + ' ' + a.firstName)?.toLowerCase() ?? '') >
        ((b.lastName + ' ' + b.firstName).toLowerCase() ?? '')
          ? 1
          : ((a.lastName + ' ' + a.firstName)?.toLowerCase() ?? '') <
              ((b.lastName + ' ' + b.firstName)?.toLowerCase() ?? '')
            ? -1
            : 0,
      );
  }

  function expand(index: number): void {
    let newExpandedItems = [...expandedItems];

    newExpandedItems.push(index);

    setExpandedItems(newExpandedItems);
  }

  function collapse(index: number): void {
    let newExpandedItems = [...expandedItems];

    let arrayIndex = newExpandedItems.indexOf(index);
    if (arrayIndex !== -1) {
      newExpandedItems.splice(arrayIndex, 1);
      setExpandedItems(newExpandedItems);
    }
  }

  function toggleFilteredStatus(status: string): void {
    let newFilteredStatus = [...filteredStatus];

    let index = newFilteredStatus.indexOf(status);
    if (index !== -1) newFilteredStatus.splice(index, 1);
    else newFilteredStatus.push(status);

    setExpandedItems([]);

    setFilteredStatus(newFilteredStatus);
  }

  function changeFilteredOrderBy(value: string): void {
    setExpandedItems([]);

    setFilteredOrderBy(value);
  }

  const renderPeopleList = () => {
    let filteredRecords =
      records
        .filter(
          p =>
            (p.firstName + ' ' + p.lastName)
              .toLowerCase()
              .includes(search.toLowerCase()) ||
            p.info.toLowerCase().includes(search.toLowerCase()),
        )
        .filter(
          p =>
            (filteredStatus.includes(PERSON_STATUSES.ACTIVE) &&
              (!p.inactiveDate ||
                p.inactiveDate > moment().startOf('day').toDate())) ||
            (filteredStatus.includes(PERSON_STATUSES.DEACTIVED) &&
              p.inactiveDate &&
              p.inactiveDate <= moment().startOf('day').toDate()) ||
            (filteredStatus.includes(PERSON_STATUSES.ACCESS) &&
              p.userStatus === PERSON_STATUSES.ACCESS) ||
            (filteredStatus.includes(PERSON_STATUSES.PENDING) &&
              p.userStatus === PERSON_STATUSES.PENDING) ||
            (filteredStatus.includes(PERSON_STATUSES.DISABLED) &&
              p.userStatus === PERSON_STATUSES.DISABLED),
        ) ?? [];

    filteredRecords = sortItems(filteredRecords);

    if (!filteredRecords || filteredRecords.length === 0) {
      return (
        <>
          <Text
            style={[
              ProjectSitesStyleSheet.siteItemText,
              {textAlign: 'center', marginTop: 24},
            ]}>
            No Results Found
          </Text>
        </>
      );
    }

    const paginator = (
      <Paginator
        key="peopleListPaginator"
        data={filteredRecords}
        pageSize={DISPLAY_INCREMENT}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        onPaginatedDataChange={setPeoplePageData}
        searchDeps={[records, search, filteredStatus, filteredOrderBy]}
      />
    );

    return (
      <View style={{gap: 16, padding: 16}}>
        {peoplePageData.map((person, i) => renderRecord(person, i))}
        <View style={{marginTop: 24}}>{paginator}</View>
      </View>
    );
  };

  const renderRecord = (
    person: PersonRecord,
    index: number,
  ): React.ReactElement => {
    return (
      <View
        key={index}
        style={{
          flexDirection: 'column',
          backgroundColor: Colors.darkGreen,
          borderRadius: 12,
          padding: 12,
        }}>
        <View style={{flexDirection: 'row'}}>
          {renderUserStatus(person)}
          <Text
            style={[
              ProjectSitesStyleSheet.siteItemText,
              {flex: 1, color: Colors.white},
            ]}
            numberOfLines={1}>
            {person.firstName + ' ' + person.lastName}
          </Text>
          {!expandedItems.includes(index) && (
            <Pressable
              style={({pressed}) => [
                {
                  width: 24,
                  height: 24,
                  borderRadius: 24,
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                pressed && {
                  backgroundColor: Colors.whiteTransparent,
                },
              ]}
              onPress={() => expand(index)}>
              <Icon icon={'expand'} color={Colors.white} size={20} />
            </Pressable>
          )}
          {expandedItems.includes(index) && (
            <Pressable
              style={({pressed}) => [
                {
                  width: 24,
                  height: 24,
                  borderRadius: 24,
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                pressed && {
                  backgroundColor: Colors.whiteTransparent,
                },
              ]}
              onPress={() => collapse(index)}>
              <Icon icon={'collapse'} color={Colors.white} size={20} />
            </Pressable>
          )}
        </View>
        {expandedItems.includes(index) ? (
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              borderTopWidth: 1,
              borderTopColor: Colors.white,
              marginTop: 6,
              paddingTop: 6,
            }}>
            <Text
              style={[
                ProjectSitesStyleSheet.siteItemText,
                {
                  fontWeight: 'normal',
                  flex: 1,
                  color: Colors.white,
                },
              ]}
              numberOfLines={1}>
              {person.info}
            </Text>
            <Pressable
              style={({pressed}) => [
                {
                  width: 24,
                  height: 24,
                  borderRadius: 24,
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                pressed && {
                  backgroundColor: Colors.whiteTransparent,
                },
              ]}
              onPress={() => goToPerson(person.id)}>
              <Icon icon={'pencil'} color={Colors.white} size={20} />
            </Pressable>
          </View>
        ) : null}
      </View>
    );
  };

  const renderUserStatus = (person: PersonRecord): React.ReactElement => {
    let icon = '';
    if (person.userStatus) {
      if (person.userStatus === PERSON_STATUSES.ACCESS)
        icon = Icons.USER_ACCESS;
      else if (person.userStatus === PERSON_STATUSES.PENDING)
        icon = Icons.USER_PENDIND;
      else if (person.userStatus === PERSON_STATUSES.DISABLED)
        icon = Icons.USER_DISABLED;
    } else {
      if (
        person.inactiveDate &&
        person.inactiveDate <= moment().startOf('day').toDate()
      )
        icon = Icons.PERSON_DEACTIVED;
      else icon = Icons.PERSON_ACTIVE;
    }

    return (
      <View style={{marginRight: 12}}>
        {icon ? (
          <img
            src={`data:image/svg+xml;utf8,${encodeURIComponent(
              icon.replaceAll('currentColor', Colors.teal),
            )}`}
            style={{color: Colors.teal}}
          />
        ) : null}
      </View>
    );
  };

  const renderFilters = (): React.ReactElement => {
    return (
      <View
        style={{
          backgroundColor: Colors.lightgray,
          borderRadius: 12,
          marginVertical: 12,
          marginRight: 12,
          padding: 24,
        }}>
        <Text style={[CommonStyleSheet.controlLabel, {alignSelf: 'center'}]}>
          Filter
        </Text>
        <View>
          <Text style={CommonStyleSheet.controlLabel}>Status</Text>
          <View style={CommonStyleSheet.filterButtonContainer}>
            <View style={CommonStyleSheet.filterButtonItem}>
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.filterButton,
                  filteredStatus.includes(PERSON_STATUSES.ACTIVE) && {
                    backgroundColor: Colors.teal,
                  },
                  pressed && {
                    opacity: 0.8,
                  },
                ]}
                onPress={() => toggleFilteredStatus(PERSON_STATUSES.ACTIVE)}>
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(
                    Icons.PERSON_ACTIVE.replaceAll(
                      'currentColor',
                      Colors.darkestGreen,
                    ),
                  )}`}
                />
              </Pressable>
              <Text style={CommonStyleSheet.filterButtonText}>
                {PERSON_STATUSES.ACTIVE}
              </Text>
            </View>
            <View style={CommonStyleSheet.filterButtonItem}>
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.filterButton,
                  filteredStatus.includes(PERSON_STATUSES.ACCESS) && {
                    backgroundColor: Colors.teal,
                  },
                  pressed && {
                    opacity: 0.8,
                  },
                ]}
                onPress={() => toggleFilteredStatus(PERSON_STATUSES.ACCESS)}>
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(
                    Icons.USER_ACCESS.replaceAll(
                      'currentColor',
                      Colors.darkestGreen,
                    ),
                  )}`}
                />
              </Pressable>
              <Text style={CommonStyleSheet.filterButtonText}>
                {PERSON_STATUSES.ACCESS}
              </Text>
            </View>
            <View style={CommonStyleSheet.filterButtonItem}>
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.filterButton,
                  filteredStatus.includes(PERSON_STATUSES.PENDING) && {
                    backgroundColor: Colors.teal,
                  },
                  pressed && {
                    opacity: 0.8,
                  },
                ]}
                onPress={() => toggleFilteredStatus(PERSON_STATUSES.PENDING)}>
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(
                    Icons.USER_PENDIND.replaceAll(
                      'currentColor',
                      Colors.darkestGreen,
                    ),
                  )}`}
                />
              </Pressable>
              <Text style={CommonStyleSheet.filterButtonText}>
                {PERSON_STATUSES.PENDING}
              </Text>
            </View>
            <View style={CommonStyleSheet.filterButtonItem}>
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.filterButton,
                  filteredStatus.includes(PERSON_STATUSES.DISABLED) && {
                    backgroundColor: Colors.teal,
                  },
                  pressed && {
                    opacity: 0.8,
                  },
                ]}
                onPress={() => toggleFilteredStatus(PERSON_STATUSES.DISABLED)}>
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(
                    Icons.USER_DISABLED.replaceAll(
                      'currentColor',
                      Colors.darkestGreen,
                    ),
                  )}`}
                />
              </Pressable>
              <Text style={CommonStyleSheet.filterButtonText}>
                {PERSON_STATUSES.DISABLED}
              </Text>
            </View>
            <View style={CommonStyleSheet.filterButtonItem}>
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.filterButton,
                  filteredStatus.includes(PERSON_STATUSES.DEACTIVED) && {
                    backgroundColor: Colors.teal,
                  },
                  pressed && {
                    opacity: 0.8,
                  },
                ]}
                onPress={() => toggleFilteredStatus(PERSON_STATUSES.DEACTIVED)}>
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(
                    Icons.PERSON_DEACTIVED.replaceAll(
                      'currentColor',
                      Colors.darkestGreen,
                    ),
                  )}`}
                />
              </Pressable>
              <Text style={CommonStyleSheet.filterButtonText}>
                {PERSON_STATUSES.DEACTIVED}
              </Text>
            </View>
          </View>
        </View>
        <View>
          <Text style={CommonStyleSheet.controlLabel}>Order By</Text>
          <DynamicSelect
            config={{
              options: [
                {label: 'First Name', value: 'First Name'},
                {label: 'Last Name', value: 'Last Name'},
              ],
            }}
            value={filteredOrderBy ? filteredOrderBy : ''}
            onChange={(controlId, controlTypeId, value) =>
              changeFilteredOrderBy(value)
            }
            style={{
              backgroundColor: 'white',
            }}
          />
        </View>
      </View>
    );
  };

  return (
    <ScrollView style={{backgroundColor: 'white'}}>
      <View>
        <ImageBackground
          source={Images.PEOPLE}
          resizeMode="cover"
          style={{height: 260}}>
          <Text style={ReportsStyleSheet.headerTitle}>People List</Text>
        </ImageBackground>
      </View>
      <View style={{flexDirection: 'row'}}>
        <View style={{flex: 2, padding: 28}}>
          <View style={{marginTop: -58, marginLeft: 'auto', marginRight: 28}}>
            <CustomPressable
              style={({pressed}) => [
                {
                  backgroundColor: Colors.green,
                  width: 58,
                  height: 58,
                  borderRadius: 58,
                },
                pressed && {
                  backgroundColor: Colors.teal,
                },
              ]}
              onPress={() => goToPerson(null)}
              tooltip="Add New">
              <View style={{marginTop: -1, marginLeft: -1}}>
                <Icon icon={'add'} color={Colors.white} size={60} />
              </View>
            </CustomPressable>
          </View>
          <SearchBox onChangeSearch={newSearch => setSearch(newSearch)} />
          {renderPeopleList()}
        </View>
        <View style={{flex: 1}}>
          {renderFilters()}
          <View style={CommonStyleSheet.bcsfFooter}>
            <Image
              source={Images.BCSF}
              style={CommonStyleSheet.bcsfFooterImage}
            />
            <Text style={CommonStyleSheet.bcsfFooterText}>
              BC <Text style={{fontWeight: '700'}}>Forest Safety</Text>
            </Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

export default PeopleList;
